import { Autocomplete, TextField } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { RobotoSwitch } from "@/shared/components/RobotoSwitch";
import { MessagePathNode, PathParser } from "@/shared/domain/topics";
import { useDebouncedCallback } from "@/shared/hooks";
import {
  type PlotSeries,
  actions,
  useVizDispatch,
} from "@/shared/state/visualization";
import {
  DEFAULT_BREAK_LINE_AT_DISCONTINUITY,
  DEFAULT_LINE_WIDTH,
  DEFAULT_POINT_RADIUS,
} from "@/shared/state/visualization/plotDefaults";
import {
  PlotSeriesConfig as PlotSeriesConfigState,
  PlotYAxis,
} from "@/shared/state/visualization/schema/v1";

import { DataSourceSelector } from "../../../controls";
import { isViewableAsPlot } from "../../../Panel/predicates";
import { default as sectionStyles } from "../../SettingsSection.module.css";
import { ConfigItem } from "../ConfigItem";

import { PlotSeriesHeader } from "./PlotSeriesHeader";
import styles from "./PlotSeriesHeader.module.css";

interface PlotSeriesConfigProps {
  order: number;
  panelId: string;
  plotSeries: PlotSeries;
  allowedYAxes: number;
}

export function PlotSeriesConfig({
  order,
  panelId,
  plotSeries,
  allowedYAxes,
}: PlotSeriesConfigProps) {
  const vizStateDispatch = useVizDispatch();

  const debouncedDispatch = useDebouncedCallback(function dispatch(
    updates: Partial<PlotSeriesConfigState>,
  ) {
    vizStateDispatch(actions.setSeriesConfig(panelId, plotSeries.id, updates));
  }, 15);
  const changeSeriesColor = (lineColor: string) =>
    debouncedDispatch({ lineColor });
  const updateConfigLineWidth = (lineWidth: number | undefined) =>
    debouncedDispatch({ lineWidth });
  const updateConfigPointRadius = (pointRadius: number | undefined) =>
    debouncedDispatch({ pointRadius });
  const updateLabel = (label: string | undefined) =>
    debouncedDispatch({ label });

  const changeSeriesYAxis = useDebouncedCallback(function setSeriesYAxis(
    yAxis: PlotYAxis | undefined,
  ) {
    if (yAxis === "y") {
      yAxis = undefined;
    }
    vizStateDispatch(
      actions.setSeriesConfig(panelId, plotSeries.id, {
        yAxis,
      }),
    );
  }, 15);

  const lineColor = plotSeries.config?.lineColor;
  const [lineWidth, setLineWidth] = React.useState(
    `${plotSeries.config?.lineWidth ?? ""}`,
  );
  const [pointRadius, setPointRadius] = React.useState(
    `${plotSeries.config?.pointRadius ?? ""}`,
  );
  const yAxis = plotSeries.config?.yAxis;
  const breakLineAtDiscontinuity =
    plotSeries.config?.breakLineAtDiscontinuity ??
    DEFAULT_BREAK_LINE_AT_DISCONTINUITY;

  const fileId = plotSeries.data.topic.association.association_id;

  const formattedMessagePath = MessagePathNode.partsToString(
    plotSeries.data.messagePath.parts,
  );
  const path = [plotSeries.data.topic.name, formattedMessagePath].join(
    PathParser.TOPIC_SEPARATOR,
  );

  const yAxisOptions: { value: PlotYAxis; label: string }[] = [
    { value: "y", label: "Default Y-Axis (Left)" },
  ];
  for (let i = 1; i < allowedYAxes; i++) {
    yAxisOptions.push({ value: `y${i}l`, label: `Y-Axis ${i} (Left)` });
    yAxisOptions.push({ value: `y${i}r`, label: `Y-Axis ${i} (Right)` });
  }

  return (
    <div className={sectionStyles.setting}>
      <PlotSeriesHeader
        order={order}
        panelId={panelId}
        plotSeries={plotSeries}
      />
      <div style={{ marginLeft: "1rem" }}>
        <ConfigItem label="Data Source">
          <DataSourceSelector
            initialFile={fileId}
            initialPath={path}
            onChange={(messagePathNode) => {
              vizStateDispatch(
                actions.setDataSource({
                  topicData: messagePathNode.toTopicData(),
                  panelId,
                  segmentId: plotSeries.id,
                }),
              );
            }}
            optionFilter={isViewableAsPlot}
          />
        </ConfigItem>
        <ConfigItem label="Label">
          <TextField
            className={sectionStyles.input}
            defaultValue={plotSeries.config?.label ?? ""}
            label="Label"
            inputProps={{
              className: sectionStyles.inputLabel,
              "aria-label": `Label for ${path}`,
              maxLength: 200,
            }}
            InputProps={{
              className: sectionStyles.inputLabel,
            }}
            InputLabelProps={{
              className: sectionStyles.inputLabel,
              shrink: true,
            }}
            placeholder={path}
            margin="dense"
            size="small"
            fullWidth
            onChange={(event) => {
              const value = event.target.value;
              updateLabel(value || undefined);
            }}
          />
        </ConfigItem>
        <ConfigItem label="Style">
          <div className={sectionStyles.inputRow}>
            <TextField
              className={classNames(sectionStyles.input, sectionStyles.noFlex)}
              variant="outlined"
              label="Color"
              margin="dense"
              size="small"
              InputLabelProps={{
                className: sectionStyles.inputLabel,
                shrink: true,
              }}
              InputProps={{
                className: sectionStyles.inputLabel,
              }}
              inputProps={{
                className: sectionStyles.inputLabel,
                "aria-label": `Change line color for ${path}`,
                type: "color",
                value: lineColor,
                name: plotSeries.id,
                onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                  changeSeriesColor(event.target.value);
                },
              }}
            />
            <TextField
              className={sectionStyles.input}
              value={lineWidth}
              label="Line Width"
              error={
                !!lineWidth && !Number.isFinite(Number.parseFloat(lineWidth))
              }
              inputProps={{
                className: sectionStyles.inputLabel,
                "aria-label": `Change line width for ${path}`,
                maxLength: 50, // 50 character limit chosen as a standard practice
              }}
              InputProps={{
                className: sectionStyles.inputLabel,
              }}
              InputLabelProps={{
                className: sectionStyles.inputLabel,
                shrink: true,
              }}
              margin="dense"
              onChange={(event) => {
                const value = event.target.value;
                setLineWidth(value);
                if (!value) {
                  updateConfigLineWidth(undefined);
                  return;
                }
                const parsed = Number.parseFloat(value);
                if (Number.isFinite(parsed)) {
                  updateConfigLineWidth(parsed);
                }
              }}
              placeholder={`${DEFAULT_LINE_WIDTH}`}
              size="small"
            />
            <TextField
              className={sectionStyles.input}
              value={pointRadius}
              label="Point Radius"
              error={
                !!pointRadius &&
                !Number.isFinite(Number.parseFloat(pointRadius))
              }
              inputProps={{
                className: sectionStyles.inputLabel,
                "aria-label": `Change point radius for ${path}`,
                maxLength: 50, // 50 character limit chosen as a standard practice
              }}
              InputProps={{
                className: sectionStyles.inputLabel,
              }}
              InputLabelProps={{
                className: sectionStyles.inputLabel,
                shrink: true,
              }}
              placeholder={`${DEFAULT_POINT_RADIUS}`}
              margin="dense"
              size="small"
              onChange={(event) => {
                const value = event.target.value;
                setPointRadius(value);
                if (!value) {
                  updateConfigPointRadius(undefined);
                  return;
                }
                const parsed = Number.parseFloat(value);
                if (Number.isFinite(parsed)) {
                  updateConfigPointRadius(parsed);
                }
              }}
            />
          </div>
          <label className={styles.switchLabel}>
            Break line at null values
            <RobotoSwitch
              checked={breakLineAtDiscontinuity}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                vizStateDispatch(
                  actions.setSeriesConfig(panelId, plotSeries.id, {
                    breakLineAtDiscontinuity: event.target.checked,
                  }),
                );
              }}
              color="success"
            />
          </label>
        </ConfigItem>
        {allowedYAxes > 1 && (
          <ConfigItem label="Y Axis">
            <Autocomplete
              fullWidth
              disableClearable
              getOptionLabel={(option) => option.label}
              size="small"
              onChange={(_, selection) => {
                if (selection === null) {
                  return;
                }
                changeSeriesYAxis(selection.value);
              }}
              options={yAxisOptions}
              value={
                yAxisOptions.find((option) => option.value === yAxis) ??
                yAxisOptions[0]
              }
              sx={{
                fontSize: "0.875rem",
                marginBottom: "0.25rem",
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    className: sectionStyles.inputLabel,
                  }}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    shrink: true,
                    className: sectionStyles.inputLabel,
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li
                  {...props}
                  className={classNames([
                    props.className,
                    sectionStyles.inputLabel,
                  ])}
                >
                  {option.label}
                </li>
              )}
            />
          </ConfigItem>
        )}
      </div>
    </div>
  );
}
